import React, { useState, useEffect, useRef } from 'react';
import { Menu, X, Phone, ChevronDown } from 'lucide-react';
import { Link } from 'react-router-dom';

const Header = ({ scrolled }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownTimeout = useRef(null);

  const solutionsDropdown = [
    { name: 'LV Systems', path: '/solutions/lv' },
    { name: 'MV Systems', path: '/solutions/mv' },
    { name: 'Solar Energy', path: '/solutions/solar' },
    { name: 'Digital Energy', path: '/solutions/digital-energy' },
    { name: 'Industrial Automation', path: '/solutions/automation' },
    { name: 'Backup Power', path: '/solutions/backup-power' }
  ];

  const productsDropdown = [
    { name: 'UPS Systems', path: '/products/ups' },
    { name: 'Solar Panels', path: '/products/solar' },
    { name: 'Generators', path: '/products/generators' },
    { name: 'Access Control', path: '/products/access-control' },
    { name: 'Security Systems', path: '/products/security' },
    { name: 'Structured Cabling', path: '/products/cabling' }
  ];

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setIsMenuOpen(false);
        setOpenDropdown(null);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleMouseEnter = (name) => {
    if (dropdownTimeout.current) {
      clearTimeout(dropdownTimeout.current);
    }
    setOpenDropdown(name);
  };

  const handleMouseLeave = () => {
    dropdownTimeout.current = setTimeout(() => {
      setOpenDropdown(null);
    }, 300);
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2' : 'bg-white py-2.5'
    }`}>
      <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
        {/* Logo - MAX SIZE for header width */}
        <Link to="/" className="flex items-center flex-shrink-0">
          <img 
            src="/images/logo/logo-header.png" 
            alt="Linear Power Systems Ltd" 
            className="h-14 md:h-20 w-auto object-contain"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = 'none';
              const parent = e.target.parentElement;
              const fallback = document.createElement('div');
              fallback.className = 'w-14 h-14 md:w-20 md:h-20 bg-[#006D77] rounded-xl flex items-center justify-center shadow-md';
              fallback.innerHTML = '<span class="text-white font-extrabold text-xl md:text-3xl">LPS</span>';
              parent.appendChild(fallback);
            }}
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-6">
          <Link to="/" className="text-gray-700 hover:text-[#006D77] font-medium transition-colors text-sm">
            Home
          </Link>
          
          <Link to="/about" className="text-gray-700 hover:text-[#006D77] font-medium transition-colors text-sm">
            About
          </Link>

          {/* Solutions Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => handleMouseEnter('solutions')}
            onMouseLeave={handleMouseLeave}
          >
            <Link 
              to="/solutions"
              className="text-gray-700 hover:text-[#006D77] font-medium transition-colors text-sm flex items-center gap-1"
            >
              Solutions
              <ChevronDown size={14} className={`transition-transform ${openDropdown === 'solutions' ? 'rotate-180' : ''}`} />
            </Link>
            {openDropdown === 'solutions' && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
                {solutionsDropdown.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#006D77]/10 hover:text-[#006D77] transition-colors border-b border-gray-50 last:border-0"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Products Dropdown */}
          <div 
            className="relative"
            onMouseEnter={() => handleMouseEnter('products')}
            onMouseLeave={handleMouseLeave}
          >
            <Link 
              to="/products"
              className="text-gray-700 hover:text-[#006D77] font-medium transition-colors text-sm flex items-center gap-1"
            >
              Products
              <ChevronDown size={14} className={`transition-transform ${openDropdown === 'products' ? 'rotate-180' : ''}`} />
            </Link>
            {openDropdown === 'products' && (
              <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden">
                {productsDropdown.map((item) => (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-[#006D77]/10 hover:text-[#006D77] transition-colors border-b border-gray-50 last:border-0"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>

          <Link to="/projects" className="text-gray-700 hover:text-[#006D77] font-medium transition-colors text-sm">
            Projects
          </Link>

          <Link
            to="/contact"
            className="bg-[#006D77] text-white px-5 py-2 rounded-full hover:bg-[#00555C] transition-all shadow-sm hover:shadow-md text-sm font-medium flex items-center gap-1.5"
          >
            <Phone size={14} />
            Contact
          </Link>
        </nav>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="Toggle menu"
        >
          {isMenuOpen ? <X size={22} className="text-gray-700" /> : <Menu size={22} className="text-gray-700" />}
        </button>
      </div>

      {/* Mobile Navigation */}
      <div className={`md:hidden transition-all duration-300 overflow-hidden ${
        isMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <nav className="bg-white border-t border-gray-100 py-3 px-6 shadow-lg">
          <div className="flex flex-col space-y-1">
            <Link to="/" className="text-gray-700 hover:text-[#006D77] font-medium py-2 px-2 hover:bg-gray-50 rounded-lg transition-colors text-sm" onClick={() => setIsMenuOpen(false)}>
              Home
            </Link>
            <Link to="/about" className="text-gray-700 hover:text-[#006D77] font-medium py-2 px-2 hover:bg-gray-50 rounded-lg transition-colors text-sm" onClick={() => setIsMenuOpen(false)}>
              About
            </Link>
            
            {/* Mobile Solutions */}
            <div className="py-1 px-2">
              <p className="text-gray-500 font-semibold text-xs uppercase tracking-wider mb-1">Solutions</p>
              {solutionsDropdown.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block py-1.5 pl-3 text-sm text-gray-600 hover:text-[#006D77] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            {/* Mobile Products */}
            <div className="py-1 px-2">
              <p className="text-gray-500 font-semibold text-xs uppercase tracking-wider mb-1">Products</p>
              {productsDropdown.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  className="block py-1.5 pl-3 text-sm text-gray-600 hover:text-[#006D77] transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </div>

            <Link to="/projects" className="text-gray-700 hover:text-[#006D77] font-medium py-2 px-2 hover:bg-gray-50 rounded-lg transition-colors text-sm" onClick={() => setIsMenuOpen(false)}>
              Projects
            </Link>
            <Link
              to="/contact"
              className="bg-[#006D77] text-white px-6 py-2 rounded-full hover:bg-[#00555C] text-center font-medium mt-1 transition-colors text-sm"
              onClick={() => setIsMenuOpen(false)}
            >
              Contact
            </Link>
          </div>
        </nav>
      </div>
    </header>
  );
};

export default Header;
